import { useEffect, useRef, memo } from "react";
import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import { Track } from "livekit-client";
import { Button } from "@/components/ui/button";
import { Maximize, Minimize } from "lucide-react";

const ScreenShareVideo = memo(function ScreenShareVideo({
  track,
}: {
  track: Track | undefined;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (videoEl && track) {
      track.attach(videoEl);
      return () => {
        track.detach(videoEl);
      };
    }
  }, [track]);

  if (!track) return null;

  return (
    <video
      className="object-contain"
      ref={videoRef}
      autoPlay
      style={{ width: "100%", height: "100%" }}
    />
  );
});

export default function ScreenShareView({
  fullScreenToggle,
  isFullScreen,
}: {
  fullScreenToggle: () => void;
  isFullScreen: boolean;
}) {
  const { livekitContext } = useLivekitContext();
  const room = livekitContext?.room;

  const screenShareParticipant = Array.from(
    room?.remoteParticipants.values() || []
  ).find((participant) =>
    participant
      .getTrackPublications()
      .some((pub) => pub.source === Track.Source.ScreenShare)
  );

  const screenSharePublication = screenShareParticipant
    ?.getTrackPublications()
    .find((pub) => pub.source === Track.Source.ScreenShare);

  const screenShareTrack = screenSharePublication?.track;
  const screenName = screenShareParticipant?.name;

  return (
    <div className="w-full h-full aspect-video my-0 relative overflow-hidden p-0.5">
      <div className="absolute top-1 left-1 z-20 flex items-center gap-1">
        {screenName && (
          <span className="p-1 px-2 backdrop-blur-md bg-gray-600/25 text-white rounded-md">
            {screenName}
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full h-9 w-9 p-2 backdrop-blur-md bg-gray-600/25 text-white"
          onClick={fullScreenToggle}
        >
          {isFullScreen ? (
            <Minimize strokeWidth={2} size={24} />
          ) : (
            <Maximize strokeWidth={2} size={24} />
          )}
        </Button>
      </div>

      <ScreenShareVideo track={screenShareTrack} />
    </div>
  );
}
